/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, InputPrice, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'
import {useMemoizedValidationSchema, useMemoizedFormValues, useMemoizedBreadcrumbs, useMemoizedPageUrl} from '../../../utils/performanceOptimizations'

import {useDispatch, useSelector} from 'react-redux'
import {allCompanyLookup, projectLookup} from '../../../redux/slices/lookup.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'
import {paymentPlanSaleInvoiceCreate} from '../../../redux/slices/paymentplan.slice'

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const selectLookupData = (state) => ({
    projectLookupList: state.lookup.projectLookupList,
    companyLookup: state.lookup.companyLookup,
    loading: state.lookup.loading
  })

  const selectCategoryData = (state) => ({
    category: state.category.category
  })

  const selectPaymentPlanData = (state) => ({
    isRedirect: state.paymentplan.isRedirect,
    loading: state.paymentplan.loading
  })

  const {projectLookupList, companyLookup, loading} = useSelector(selectLookupData)
  const {category} = useSelector(selectCategoryData)
  const {isRedirect, loading: createLoading} = useSelector(selectPaymentPlanData)

  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(categoryGetAll({SourceTypeId: 29}))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    TotalPrice: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    DefermentDate: new Date(),
    TotalPrice: 0,
    CompanyId: null,
    CategoryId: null,
    Description: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(paymentPlanSaleInvoiceCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.CASH.SALE'}),
      path: '/cash/sale',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.CASH.SALE.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.SALE.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.PROJECTNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.DEFERMENTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('DefermentDate')}
                  onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.TOTALPRICE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('TotalPrice')}
                  onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                />
                {formik.touched.TotalPrice && formik.errors.TotalPrice && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.TotalPrice}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.COMPANYNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={companyLookup}
                  required
                  {...formik.getFieldProps('CompanyId')}
                  onChange={(item) => formik.setFieldValue('CompanyId', Number(item && item.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.CATEGORYNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={category}
                  required
                  {...formik.getFieldProps('CategoryId')}
                  onChange={(item) => formik.setFieldValue('CategoryId', Number(item && item.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.DESCRIPTION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  required
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Description')}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
